/**
 * UnifiedManualEntryModal - Modal with toggle to switch between water test and dosing forms.
 *
 * Features:
 * - SegmentedControl toggle at top for switching between entry types
 * - Renders WaterTestForm or DosingForm based on selection
 * - Success view after logging with "Log Another" and "Done" buttons
 * - After water test success, checks for matching testing tasks and offers completion
 * - Resets state when modal closes (with animation delay)
 */
import { useState, useEffect } from 'react';
import {
  Modal,
  Drawer,
  Stack,
  SegmentedControl,
  Group,
  Text,
  ThemeIcon,
  Button,
  Paper,
  Loader,
} from '@mantine/core';
import { IconDroplet, IconFlask, IconCheck, IconChecklist } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useTank } from '@/contexts/TankContext';
import { WaterTestForm } from './WaterTestForm';
import { DosingForm } from './DosingForm';
import { fetchMatchingTestTask, tasksApi } from '@/api/tasks';
import type { MatchingTestTask } from '@/api/chat';
import type { ManualMeasurementRequest, TesterBrand } from '@/api/measurements';

type EntryType = 'water-test' | 'dosing';

export interface UnifiedManualEntryModalProps {
  /** Whether the modal is open */
  opened: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Initial form to show (defaults to 'water-test') */
  initialType?: EntryType;
  /** Pre-select a parameter key in the water test form (e.g., 'no3') */
  defaultParameterKey?: string;
}

/**
 * State for displaying success confirmation
 */
interface SuccessState {
  type: EntryType;
  // Water test success data
  waterTestEntry?: ManualMeasurementRequest;
  parameterName?: string;
  unit?: string;
  tester?: TesterBrand | null;
  // Dosing success data
  chemicalName?: string;
  volumeMl?: number;
  brand?: string;
}

export function UnifiedManualEntryModal({
  opened,
  onClose,
  initialType = 'water-test',
  defaultParameterKey,
}: UnifiedManualEntryModalProps) {
  const [entryType, setEntryType] = useState<EntryType>(initialType);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] = useState<SuccessState | null>(null);
  const isMobile = useIsMobile();

  // Reset state when modal closes
  const handleClose = () => {
    onClose();
    // Delay reset to allow close animation
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessData(null);
    }, 200);
  };

  // Handle water test success
  const handleWaterTestSuccess = (
    entry: ManualMeasurementRequest,
    parameterName: string,
    unit: string,
    tester: TesterBrand | null
  ) => {
    setSuccessData({
      type: 'water-test',
      waterTestEntry: entry,
      parameterName,
      unit,
      tester,
    });
    setShowSuccess(true);
  };

  // Handle dosing success
  const handleDosingSuccess = (
    chemicalName: string,
    volumeMl: number,
    brand?: string
  ) => {
    setSuccessData({
      type: 'dosing',
      chemicalName,
      volumeMl,
      brand,
    });
    setShowSuccess(true);
  };

  // Handle "Log Another" button
  const handleLogAnother = () => {
    setShowSuccess(false);
    setSuccessData(null);
  };

  const title = (
    <Group gap="xs">
      {entryType === 'water-test' ? (
        <IconDroplet size={20} />
      ) : (
        <IconFlask size={20} />
      )}
      <Text fw={600}>Log Data</Text>
    </Group>
  );

  const content = showSuccess && successData ? (
    <SuccessView
      data={successData}
      onLogAnother={handleLogAnother}
      onClose={handleClose}
    />
  ) : (
    <Stack gap="md">
      {/* Toggle between Water Test and Dosing */}
      <SegmentedControl
        value={entryType}
        onChange={(val) => setEntryType(val as EntryType)}
        data={[
          { label: 'Water Test', value: 'water-test' },
          { label: 'Dosing', value: 'dosing' },
        ]}
        fullWidth
      />

      {/* Render appropriate form */}
      {entryType === 'water-test' ? (
        <WaterTestForm onSuccess={handleWaterTestSuccess} defaultParameterKey={defaultParameterKey} />
      ) : (
        <DosingForm onSuccess={handleDosingSuccess} />
      )}
    </Stack>
  );

  return isMobile ? (
    <Drawer
      opened={opened}
      onClose={handleClose}
      title={title}
      position="bottom"
      size="85%"
      styles={{
        body: {
          paddingBottom: 'env(safe-area-inset-bottom, 16px)',
        },
      }}
    >
      {content}
    </Drawer>
  ) : (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={title}
      size="md"
      centered
      transitionProps={{ transition: 'slide-up', duration: 200 }}
    >
      {content}
    </Modal>
  );
}

/**
 * Success view shown after logging an entry.
 * For water tests, also checks for matching testing tasks and offers to mark them complete.
 */
interface SuccessViewProps {
  data: SuccessState;
  onLogAnother: () => void;
  onClose: () => void;
}

function SuccessView({ data, onLogAnother, onClose }: SuccessViewProps) {
  const isWaterTest = data.type === 'water-test';
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;
  const queryClient = useQueryClient();

  const [matchingTask, setMatchingTask] = useState<MatchingTestTask | null>(null);
  const [taskState, setTaskState] = useState<'checking' | 'prompt' | 'completing' | 'completed' | 'none'>('checking');

  // Fetch matching task for water tests
  useEffect(() => {
    if (!isWaterTest || !tankId || !data.waterTestEntry?.parameter_key) {
      setTaskState('none');
      return;
    }

    fetchMatchingTestTask(tankId, data.waterTestEntry.parameter_key)
      .then((match) => {
        if (match) {
          setMatchingTask(match);
          setTaskState('prompt');
        } else {
          setTaskState('none');
        }
      })
      .catch(() => {
        setTaskState('none');
      });
  }, [isWaterTest, tankId, data.waterTestEntry?.parameter_key]);

  const handleMarkComplete = async () => {
    if (!matchingTask || !tankId || !data.waterTestEntry) return;
    setTaskState('completing');
    try {
      await tasksApi.completeInstance(tankId, matchingTask.instance_id, {
        value: data.waterTestEntry.value,
        completed_at: data.waterTestEntry.recorded_at,
      });
      setTaskState('completed');
      queryClient.invalidateQueries({ queryKey: ['task-instances', tankId] });
      queryClient.invalidateQueries({ queryKey: ['task-definitions', tankId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', tankId] });
    } catch {
      setTaskState('prompt'); // Allow retry
    }
  };

  const dueText = matchingTask
    ? matchingTask.is_overdue
      ? `overdue since ${formatDate(matchingTask.due_at)}`
      : `due ${formatDate(matchingTask.due_at)}`
    : '';

  return (
    <Stack gap="md" align="center" py="md">
      <ThemeIcon size={48} color="green" variant="light" radius="xl">
        <IconCheck size={28} />
      </ThemeIcon>

      <Text fw={500}>
        {isWaterTest ? 'Water test logged!' : 'Dosing entry logged!'}
      </Text>

      <Text size="sm" c="dimmed">
        {isWaterTest
          ? `${data.parameterName}: ${data.waterTestEntry?.value} ${data.unit}`
          : `${data.chemicalName}: ${data.volumeMl}ml${data.brand ? ` (${data.brand})` : ''}`}
      </Text>

      {/* Task matching: checking state */}
      {isWaterTest && taskState === 'checking' && (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="xs" c="dimmed">Checking for related tasks...</Text>
        </Group>
      )}

      {/* Task matching: prompt state */}
      {taskState === 'prompt' && matchingTask && (
        <Paper p="sm" radius="md" bg="blue.0" w="100%">
          <Stack gap="xs">
            <Group gap="xs">
              <IconChecklist size={16} />
              <Text size="sm" fw={500}>
                You have a scheduled task for this ({dueText})
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              {matchingTask.definition_status === 'active' && matchingTask.has_recurrence && matchingTask.next_due_preview
                ? `You can leave it as is, or mark it complete and schedule the next one for ${formatDate(matchingTask.next_due_preview)}.`
                : 'You can leave it as is, or mark it complete.'}
            </Text>
            <Group gap="xs">
              <Button size="xs" variant="filled" onClick={handleMarkComplete}>
                Mark complete
              </Button>
              <Button size="xs" variant="subtle" onClick={() => setTaskState('none')}>
                Leave alone
              </Button>
            </Group>
          </Stack>
        </Paper>
      )}

      {/* Task matching: completing state */}
      {taskState === 'completing' && (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="xs" c="dimmed">Completing task...</Text>
        </Group>
      )}

      {/* Task matching: completed state */}
      {taskState === 'completed' && matchingTask && (
        <Paper p="sm" radius="md" bg="green.0" w="100%">
          <Group gap="xs">
            <IconCheck size={16} />
            <Text size="sm" fw={500}>Task completed!</Text>
          </Group>
          {matchingTask.definition_status === 'active' && matchingTask.has_recurrence && matchingTask.next_due_preview && (
            <Text size="xs" c="dimmed">
              Next test scheduled for {formatDate(matchingTask.next_due_preview)}.
            </Text>
          )}
        </Paper>
      )}

      <Group>
        <Button variant="light" onClick={onLogAnother}>
          Log Another
        </Button>
        <Button onClick={onClose}>Done</Button>
      </Group>
    </Stack>
  );
}

/**
 * Format a date string for display in task matching UI.
 */
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}
